import React from 'react';
import { Badge } from '../ui/Badge';
import { Globe, Shield, Map, Server, Cloud, Activity, Eye } from 'lucide-react';
import { AssetActions } from './AssetActions';
import { TechnologyStack } from './TechnologyStack';
import * as Tooltip from '@radix-ui/react-tooltip';
import { TablePagination } from '../ui/TablePagination';
import { Pagination } from '../ui/Pagination';
import { Link, useLocation } from 'react-router-dom';

interface IpAddress {
  id: string;
  ipAddress: string;
  hostname?: string;
  isActive: boolean;
  riskScore: string;
  updatedAt: string;
  createdAt: string;
  tags: string[];
  technologies: Array<{
    name: string;
    version: string;
    icon: string;
    color: string;
  }>;
  location: {
    country?: string;
    city?: string;
    region?: string;
    postal?: string;
    timezone?: string;
    coordinates?: string;
  };
  network: {
    org?: string;
    asn?: string;
    cdn?: boolean;
    cdnName?: string;
    waf?: boolean;
    wafName?: string;
  };
  openPorts?: Array<{
    port: number;
    protocol: string;
    service?: string;
    state: string;
  }>;
}

interface PaginationInfo {
  count: number;
  next: string | null;
  previous: string | null;
}

interface IpAddressesTableProps {
  ipAddresses: IpAddress[];
  currentPage: number;
  onPageChange: (page: number) => void;
  totalIpAddresses: number;
  itemsPerPage: number;
  paginationInfo: PaginationInfo;
}

export const IpAddressesTable: React.FC<IpAddressesTableProps> = ({
  ipAddresses,
  currentPage,
  onPageChange,
  totalIpAddresses,
  itemsPerPage,
  paginationInfo,
}) => {
  const startIndex = (currentPage - 1) * itemsPerPage + 1;
  const endIndex = Math.min(currentPage * itemsPerPage, totalIpAddresses);

  const getRiskColor = (score: string) => {
    switch (score) {
      case "A+": return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      case "A": return 'text-green-500 bg-green-500/10 border-green-500/20';
      case "B": return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20';
      case "C": return 'text-orange-500 bg-orange-500/10 border-orange-500/20';
      case "F": return 'text-red-500 bg-red-500/10 border-red-500/20';
      default: return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
    }
  };

  const getLocationString = (location: IpAddress['location']) => {
    const parts = [];
    if (location.city) parts.push(location.city);
    if (location.region) parts.push(location.region);
    if (location.country) parts.push(location.country);
    return parts.join(', ') || 'Unknown location';
  };

  return (
    <div className="bg-[#0F1629] rounded-xl overflow-hidden border border-gray-800/50 flex flex-col">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-800">
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">IP Address</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Technologies</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Status</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Risk Score</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Location</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Network Info</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Open Ports</th>
              <th className="px-6 py-4 text-left text-sm font-medium text-gray-400">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-800">
            {ipAddresses.map((ip) => (
              <tr key={ip.id} className="group hover:bg-gray-800/30">
                <td className="px-6 py-4">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 rounded-lg bg-gray-800/30 group-hover:bg-gray-800/50 transition-colors">
                      <Server className="h-5 w-5 text-gray-400" />
                    </div>
                    <div>
                    <Link to={`/ip-addresses/${ip.id}`} className="text-sm font-medium text-white">{ip.ipAddress}</Link>
                    {ip.hostname && (
                        <div className="text-sm text-gray-400">{ip.hostname}</div>
                      )}
                      <div className="flex items-center space-x-2 mt-1 flex-wrap">
                        {ip.tags.map((tag, index) => (
                          <span
                            key={index}
                            className="px-2 py-0.5 text-xs rounded-full bg-gray-800/50 text-gray-400 hover:bg-gray-800 hover:text-gray-300 transition-colors cursor-pointer"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <TechnologyStack
                    technologies={ip.technologies}
                    maxVisible={3}
                  />
                </td>
                <td className="px-6 py-4">
                  <Badge
                    className={ip.isActive ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' : 'bg-red-500/10 text-red-400 border-red-500/20'}
                  >
                    {ip.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <Badge className={getRiskColor(ip.riskScore?.toUpperCase() || 'Unknown')}>
                    {ip.riskScore?.toUpperCase() || 'Unknown'}
                  </Badge>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-2">
                    {getLocationString(ip.location) !== 'Unknown location' && (
                      <div className="flex items-center space-x-2">
                        <Map className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-300">
                          {getLocationString(ip.location)}
                        </span>
                      </div>
                    )}
                    {ip.location.timezone && (
                      <div className="text-xs text-gray-400">
                        TZ: {ip.location.timezone}
                      </div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-2">
                    {ip.network.org && (
                      <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20">
                        {ip.network.org}
                      </Badge>
                    )}
                    {ip.network.asn && (
                      <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20">
                        ASN: {ip.network.asn}
                      </Badge>
                    )}
                    {ip.network.cdn && (
                      <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                        CDN: {ip.network.cdnName || 'Yes'}
                      </Badge>
                    )}
                    {ip.network.waf && (
                      <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                        WAF: {ip.network.wafName || 'Yes'}
                      </Badge>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex flex-col space-y-1">
                    {ip.openPorts?.map((port, index) => (
                      <Tooltip.Provider key={index}>
                        <Tooltip.Root>
                          <Tooltip.Trigger asChild>
                            <Badge className="bg-gray-700/50 text-gray-300 border-gray-600/20 cursor-help">
                              <div className="flex items-center space-x-1">
                                <Activity className="h-3 w-3" />
                                <span>{port.port}/{port.protocol.toUpperCase()}</span>
                              </div>
                            </Badge>
                          </Tooltip.Trigger>
                          <Tooltip.Portal>
                            <Tooltip.Content
                              className="px-3 py-1.5 rounded-lg bg-[#1A1F2E] border border-gray-800 text-xs text-white shadow-xl"
                              sideOffset={5}
                            >
                              <div className="space-y-1">
                                <div>Service: {port.service || 'Unknown'}</div>
                                <div>State: {port.state}</div>
                              </div>
                              <Tooltip.Arrow className="fill-[#1A1F2E]" />
                            </Tooltip.Content>
                          </Tooltip.Portal>
                        </Tooltip.Root>
                      </Tooltip.Provider>
                    ))}
                  </div>
                </td>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-2">
                    <Link
                      to={`/ip-addresses/${ip.id}`}
                      className="bg-[#1A1F2E] text-white"
                    >
                      <Eye className="h-4 w-4" />
                    </Link>
                    <AssetActions assetId={ip.id} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePagination
        currentPage={currentPage}
        onPageChange={onPageChange}
        totalItems={totalIpAddresses}
        itemsPerPage={itemsPerPage}
        paginationInfo={paginationInfo}
        itemName="IP addresses"
      />
    </div>
  );
};
